import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  Brain, Loader2, Copy, CheckCircle, MessageSquare, Lightbulb,
  ChevronDown, ChevronUp, Settings, Zap
} from "lucide-react";

export default function ContentGenerator({ projects, selectedProject }) {
  const { api } = useAuth();
  const [mode, setMode] = useState("comment");
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [websiteContext, setWebsiteContext] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [isContextFallback, setIsContextFallback] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  // Comment generation state
  const [postTitle, setPostTitle] = useState("");
  const [postBody, setPostBody] = useState("");
  const [subreddit, setSubreddit] = useState("");
  const [generatedComment, setGeneratedComment] = useState("");

  // Post ideas state
  const [targetSubreddits, setTargetSubreddits] = useState("");
  const [ideaCount, setIdeaCount] = useState(3);
  const [generatedIdeas, setGeneratedIdeas] = useState([]);
  const [expandedIdea, setExpandedIdea] = useState(null);

  // Post analysis state
  const [analysisResult, setAnalysisResult] = useState(null);

  useEffect(() => {
    fetchModels();
  }, []);

  useEffect(() => {
    if (selectedProject) fetchContext();
  }, [selectedProject]);

  const fetchModels = async () => {
    try {
      const res = await api.get("/llm/models");
      setModels(res.data.models || []);
      setSelectedModel(res.data.default || "");
    } catch {}
  };

  const fetchContext = async () => {
    try {
      const res = await api.get(`/llm/website-context/${selectedProject}`);
      setWebsiteContext(res.data.website_context || "");
      setWebsiteUrl(res.data.website || "");
      setIsContextFallback(!!res.data.is_fallback);
    } catch {}
  };

  const handleGenerateComment = async () => {
    if (!postTitle.trim()) return;
    setLoading(true); setGeneratedComment("");
    try {
      const res = await api.post("/llm/generate-comment", {
        title: postTitle,
        body: postBody,
        subreddit,
        website_context: websiteContext,
        website_url: websiteUrl,
        model: selectedModel || undefined,
      });
      setGeneratedComment(res.data.comment);
    } catch (err) {
      setGeneratedComment(`Error: ${err.response?.data?.detail || "Generation failed"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzePost = async () => {
    if (!postTitle.trim()) return;
    setLoading(true); setAnalysisResult(null);
    try {
      const res = await api.post("/llm/analyze-post", {
        title: postTitle,
        body: postBody,
        subreddit,
        website_context: websiteContext,
        model: selectedModel || undefined,
      });
      setAnalysisResult(res.data);
    } catch (err) {
      setAnalysisResult({ error: err.response?.data?.detail || "Analysis failed" });
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestIdeas = async () => {
    setLoading(true); setGeneratedIdeas([]);
    try {
      const subs = targetSubreddits.split(",").map(s => s.trim().replace(/^r\//, "")).filter(Boolean);
      const res = await api.post("/llm/suggest-post-ideas", {
        website_context: websiteContext,
        website_url: websiteUrl,
        target_subreddits: subs,
        count: ideaCount,
        model: selectedModel || undefined,
      });
      setGeneratedIdeas(res.data.ideas || []);
    } catch (err) {
      setGeneratedIdeas([{ title: "Error", body: err.response?.data?.detail || "Failed to generate ideas", subreddit: "" }]);
    } finally {
      setLoading(false);
    }
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="content-generator" data-testid="content-generator">
      <div className="generator-header">
        <h2 className="section-title"><Brain size={20} color="#ff4500" /> AI Content Generator</h2>
        <div className="model-selector" data-testid="model-selector">
          <Settings size={13} />
          <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)} className="filter-select" data-testid="model-select">
            {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      </div>

      <div className="mode-tabs" data-testid="generator-mode-tabs">
        <button className={`mode-tab ${mode === 'comment' ? 'active' : ''}`} onClick={() => setMode('comment')} data-testid="mode-comment">
          <MessageSquare size={14} /> Generate Comment
        </button>
        <button className={`mode-tab ${mode === 'analyze' ? 'active' : ''}`} onClick={() => setMode('analyze')} data-testid="mode-analyze">
          <Zap size={14} /> Analyze Post
        </button>
        <button className={`mode-tab ${mode === 'ideas' ? 'active' : ''}`} onClick={() => setMode('ideas')} data-testid="mode-ideas">
          <Lightbulb size={14} /> Post Ideas
        </button>
      </div>

      {(mode === "comment" || mode === "analyze") && (
        <div className="generator-form" data-testid="post-input-form">
          <div className="form-row">
            <div className="form-field">
              <label>Subreddit</label>
              <input className="filter-input" placeholder="e.g. software, sysadmin" value={subreddit}
                onChange={e => setSubreddit(e.target.value)} data-testid="gen-subreddit" />
            </div>
          </div>
          <div className="form-field">
            <label>Post Title</label>
            <input className="filter-input" placeholder="Reddit post title..." value={postTitle}
              onChange={e => setPostTitle(e.target.value)} data-testid="gen-post-title" />
          </div>
          <div className="form-field">
            <label>Post Body (optional)</label>
            <textarea className="filter-input settings-textarea" rows={3} placeholder="Post body text..."
              value={postBody} onChange={e => setPostBody(e.target.value)} data-testid="gen-post-body" />
          </div>
          <div className="form-field">
            <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Website Context</span>
              {isContextFallback && selectedProject && (
                <span style={{ fontSize: 11, color: "#f59e0b" }} data-testid="context-fallback-hint">
                  Auto-filled from project basics. Set a custom one in Project Settings →
                </span>
              )}
            </label>
            <textarea className="filter-input settings-textarea" rows={2} placeholder="Context about your website..."
              value={websiteContext} onChange={e => setWebsiteContext(e.target.value)} data-testid="gen-context" />
          </div>

          {mode === "comment" && (
            <>
              <button className="btn btn-primary" onClick={handleGenerateComment} disabled={loading || !postTitle.trim()} data-testid="generate-comment-btn">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Brain size={14} />}
                {loading ? 'Generating...' : 'Generate Comment'}
              </button>
              {generatedComment && (
                <div className="generated-output" data-testid="generated-comment">
                  <div className="output-header">
                    <span>Generated Comment</span>
                    <button className="copy-btn" onClick={() => copyText(generatedComment, 'comment')} data-testid="copy-comment">
                      {copied === 'comment' ? <CheckCircle size={14} color="#10b981" /> : <Copy size={14} />}
                    </button>
                  </div>
                  <div className="output-text">{generatedComment}</div>
                </div>
              )}
            </>
          )}

          {mode === "analyze" && (
            <>
              <button className="btn btn-primary" onClick={handleAnalyzePost} disabled={loading || !postTitle.trim()} data-testid="analyze-post-btn">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                {loading ? 'Analyzing...' : 'Analyze Post'}
              </button>
              {analysisResult && (
                <div className="analysis-output" data-testid="analysis-result">
                  {analysisResult.error ? (
                    <div className="alert alert-error">{analysisResult.error}</div>
                  ) : (
                    <>
                      <div className={`suitability-badge ${analysisResult.suitable ? 'suitable' : 'not-suitable'}`}>
                        {analysisResult.suitable ? <CheckCircle size={14} /> : null}
                        {analysisResult.suitable ? 'Suitable for Marketing' : 'Not Suitable'}
                      </div>
                      <div className="analysis-details">
                        <div className="analysis-row"><strong>Relevance:</strong> <span className={`score-badge ${analysisResult.relevance_score >= 7 ? 'high' : analysisResult.relevance_score >= 4 ? 'mid' : 'low'}`}>{analysisResult.relevance_score}/10</span></div>
                        <div className="analysis-row"><strong>Reason:</strong> {analysisResult.reason}</div>
                        {analysisResult.suggested_angle && <div className="analysis-row"><strong>Suggested Angle:</strong> {analysisResult.suggested_angle}</div>}
                        <div className="analysis-row" style={{fontSize:11,color:'#6b7280'}}>Model: {analysisResult.model_used}</div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {mode === "ideas" && (
        <div className="generator-form" data-testid="ideas-form">
          <div className="form-row">
            <div className="form-field">
              <label>Target Subreddits (comma-separated)</label>
              <input className="filter-input" placeholder="e.g. software, sysadmin, windows"
                value={targetSubreddits} onChange={e => setTargetSubreddits(e.target.value)} data-testid="ideas-subreddits" />
            </div>
            <div className="form-field" style={{maxWidth:120}}>
              <label>Count</label>
              <select className="filter-select" value={ideaCount} onChange={e => setIdeaCount(Number(e.target.value))} data-testid="ideas-count">
                <option value={2}>2</option><option value={3}>3</option><option value={5}>5</option>
              </select>
            </div>
          </div>
          <div className="form-field">
            <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Website Context</span>
              {isContextFallback && selectedProject && (
                <span style={{ fontSize: 11, color: "#f59e0b" }}>
                  Auto-filled from project basics. Set a custom one in Project Settings →
                </span>
              )}
            </label>
            <textarea className="filter-input settings-textarea" rows={2} placeholder="Context about your website..."
              value={websiteContext} onChange={e => setWebsiteContext(e.target.value)} data-testid="ideas-context" />
          </div>
          <button className="btn btn-primary" onClick={handleSuggestIdeas} disabled={loading} data-testid="generate-ideas-btn">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Lightbulb size={14} />}
            {loading ? 'Generating...' : 'Generate Post Ideas'}
          </button>

          {generatedIdeas.length > 0 && (
            <div className="ideas-list" data-testid="ideas-list">
              {generatedIdeas.map((idea, idx) => (
                <div key={idx} className="idea-card" data-testid={`idea-${idx}`}>
                  <div className="idea-header" onClick={() => setExpandedIdea(expandedIdea === idx ? null : idx)}>
                    <div className="idea-info">
                      <span className="subreddit-cell">r/{idea.subreddit}</span>
                      <span className="idea-type">{idea.post_type}</span>
                    </div>
                    <div className="idea-title">{idea.title}</div>
                    <div className="idea-actions">
                      <button className="copy-btn" onClick={(e) => { e.stopPropagation(); copyText(`${idea.title}\n\n${idea.body}`, `idea-${idx}`); }}>
                        {copied === `idea-${idx}` ? <CheckCircle size={14} color="#10b981" /> : <Copy size={14} />}
                      </button>
                      {expandedIdea === idx ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                  </div>
                  {expandedIdea === idx && (
                    <div className="idea-body">
                      <div className="idea-text">{idea.body}</div>
                      {idea.marketing_angle && (
                        <div className="idea-angle"><strong>Marketing Angle:</strong> {idea.marketing_angle}</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
